import React from 'react'
import { INLINES, BLOCKS, MARKS } from '@contentful/rich-text-types'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import HiddenInfogram from '@toasttab/shared/src/components/molecules/HiddenInfogram/HiddenInfogram'
import InfogramEmbed from '@toasttab/shared/src/components/molecules/InfogramEmbed/InfogramEmbed'
import ProductCTA from '@toasttab/otl/src/components/PageModules/OTLContentSections/ProductCTA/ProductCTA'
import TechTips from '@toasttab/otl/src/components/PageModules/OTLContentSections/TechTips/TechTips'
import useHasMounted from '@toasttab/shared/src/hooks/useHasMounted'

const getFormDisclaimerVariation = (node: any) => {
  if (node?.data?.uri?.includes('/privacy')) {
    return 'caption'
  } else {
    return 'default'
  }
}

export const MicrocopyOptions = {
  renderNode: {
    [INLINES.EMBEDDED_ENTRY]: (node: any, children: any) => {
      return <>{node.data.target?.content}</>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-secondary'
          variation='default'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_2]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography allowHtml={false} as='h2' classes='mb-6' variation='h2'>
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const FormStepStatementOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mb-4 text-secondary'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a className='font-inherit text-inherit underline' href={uri}>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-secondary text-[14px]'
          variation='statement'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const FormDisclaimerOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mt-4 text-secondary'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a
          className='font-normal underline whitespace-nowrap md:whitespace-normal'
          href={uri}
        >
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      // Configure different styling for the Login block and the Privacy Statement block
      const linkNode = node?.content?.find(
        (node: any) => node.nodeType === 'hyperlink'
      )
      const variation = linkNode
        ? getFormDisclaimerVariation(linkNode)
        : 'statement'

      return (
        <Typography allowHtml={false} as='p' variation={variation}>
          {children}
        </Typography>
      )
    }
  }
}

export const FormDisclaimerOptionsInverse = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mt-4 text-white'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a
          className='font-inherit text-inherit text-white underline'
          href={uri}
        >
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      // Configure different styling for the Login block and the Privacy Statement block
      const linkNode = node?.content?.find(
        (node: any) => node.nodeType === 'hyperlink'
      )
      const variation = linkNode
        ? getFormDisclaimerVariation(linkNode)
        : 'statement'

      return (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-white'
          variation={variation}
        >
          {children}
        </Typography>
      )
    }
  }
}

export const FormDisclaimerEmailSignupOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mt-6 text-secondary'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a className='font-inherit text-inherit' href={uri}>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-secondary type-caption'
          variation='statement'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const FormDisclaimerOptionsEmailSignupInverse = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mt-6 text-white'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a className='font-inherit text-inherit text-white' href={uri}>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-white type-caption'
          variation='statement'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const InformationCardHeadingOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography allowHtml={false} as='p' classes='text-gray-100'>
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const TestimonialHeadingOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-center md:text-left mb-4 !font-normal w-full'
          variation='h4'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='h4'
          classes='text-center md:text-left mb-4 w-full'
          variation='h4'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='h3'
          classes='text-center md:text-left mb-4 w-full'
          variation='h3'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const TestimonialInlineQuoteHeadingOptions = (textAlign = 'center') => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='p'
          variation='h4'
          classes={`text-${textAlign} mb-4 !font-normal`}
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='h3'
          variation='h3'
          classes={`text-${textAlign} mb-4 !font-normal`}
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='h4'
          variation='h4'
          classes={`text-${textAlign} mb-4 !font-normal`}
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
})

export const FootnotesOptions = {
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      const id = uri.startsWith('id:') && uri.replace('id:', '')
      const isAnchor = uri.startsWith('id:')
      if (isAnchor) {
        return <span id={id}>{children}</span>
      }
      return (
        <a className='underline font-normal' href={uri}>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0][1] !== '' ? (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-secondary [&>a]:!type-footnote'
          variation='footnote'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.OL_LIST]: (node: any, children: any) => {
      return (
        <ol className='list-decimal list-outside pl-4 marker:text-secondary marker:!type-footnote'>
          {children}
        </ol>
      )
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='text-secondary !type-footnote mb-3'>{children}</li>
    }
  }
}

export const StyledListOptions = (listStyle?: String) => ({
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderMark: {
    [MARKS.SUPERSCRIPT]: (text: any) => {
      return (
        <sub className={`leading-[0] align-super text-[10px] ${listStyle}`}>
          {text}
        </sub>
      )
    },
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold text-default'>{text}</span>
    }
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a className='font-normal' href={uri}>
          {children}
        </a>
      )
    }
  }
})

export const BasicCardOptions = {
  renderNode: {
    [INLINES.EMBEDDED_ENTRY]: (node: any, children: any) => {
      return <>{node.data.target?.content}</>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-gray-100'
          variation='default'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const BenchmarkingFormDescriptionOptions = {
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderNode: {
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ol className=''>{children}</ol>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return (
        <li className='text-secondary text-[14px] mb-1 inline-flex'>
          <svg
            className='inline basis-4 flex-shrink-0 mr-4 mt-1'
            fill='none'
            height='16'
            viewBox='0 0 24 24'
            width='16'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M4.00049 12.7498L9.60049 18.9498L20.0005 5.0498'
              stroke-linecap='round'
              stroke-linejoin='round'
              stroke-miterlimit='10'
              stroke-width='1.5'
              stroke='#ff4c00'
            />
          </svg>
          {children}
        </li>
      )
    }
  }
}

export const BasicListOptions = {
  renderNode: {
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ul className='list-disc list-inside pl-4'>{children}</ul>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='[&>p]:inline'>{children}</li>
    }
  }
}

export const OTLTrackBodyOptions = {
  renderNode: {
    [INLINES.EMBEDDED_ENTRY]: (node: any, children: any) => {
      return <>{node.data.target?.content}</>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography allowHtml={false} as='p' classes='pb-4' variation='default'>
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const GeneralBodyOptions = {
  renderNode: {
    table: (node: any, children: any) => {
      let rowIndex = 0 // Keep track of row index manually

      // Map through the rows and apply custom class to the first row
      const renderedRows = React.Children.map(children, (child) => {
        const isFirstRow = rowIndex === 0
        const className = isFirstRow ? '!font-bold' : ''

        // Increment the row index after each row
        rowIndex += 1

        // Wrap the row content with a <tr> and apply the class
        return <tr className={className}>{child}</tr>
      })

      return (
        <table
          className='custom-table'
          style={{
            border: '1px solid black',
            width: '100%',
            borderCollapse: 'collapse'
          }}
        >
          <tbody>{renderedRows}</tbody>
        </table>
      )
    },
    'table-row': (node: any, children: any) => {
      return <>{children}</>
    },
    'table-cell': (node: any, children: any) => {
      return (
        <td
          className='custom-table-cell'
          style={{ padding: '5pt', border: '1px solid black' }}
        >
          {React.Children.map(children, (child) => {
            if (child.type === 'p') {
              // Apply inline style to <p> tags to remove margin-top
              return React.cloneElement(child, {
                style: { marginTop: 0 }
              })
            }
            return child
          })}
        </td>
      )
    },
    // 'table-header-cell': (node: any, children: any) => {
    //   return <th className="!font-bold">{children}</th>
    // },
    // bartending-competitions has PICS
    // gas-station-ideas-and-concepts , 4th-of-july-food- has PICS
    [BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
      const asset = node.data.target
      const entryType = asset?.__typename
      if (entryType && entryType === 'ContentfulAsset') {
        const { url, contentType } = asset.file
        const isImage = contentType.includes('image')
        if (isImage) {
          return (
            <p className='rich-text-asset'>
              <img
                alt={asset.description || asset.title || ''}
                src={url}
                title={asset.title || ''}
              />
            </p>
          )
        }
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      const entry = node.data.target
      const entryId = node.data.target?.sys.id
      const entryType = entry?.__typename
      const wrapperStyles = 'my-6'
      if (entry && entryId && entryType === 'ContentfulInfogramEmbed') {
        return (
          <div className={wrapperStyles}>
            <InfogramEmbed {...entry} />
          </div>
        )
      }
      if (entry && entryId && entryType === 'ContentfulHiddenInfogram') {
        return (
          <div className={wrapperStyles}>
            <HiddenInfogram {...entry} />
          </div>
        )
      }
      if (entry && entryId && entryType === 'ContentfulProductCta') {
        return (
          <div className={wrapperStyles}>
            <ProductCTA {...entry} />
          </div>
        )
      }
      if (entry && entryId && entryType === 'ContentfulTechTips') {
        return (
          <div className={wrapperStyles}>
            <TechTips {...entry} />
          </div>
        )
      }
    },
    // 10 BEST and Most Profitable Bar Foods and Menu Items - has IG
    [INLINES.EMBEDDED_ENTRY]: (node: any) => {
      const hasMounted = useHasMounted()
      const entry = node.data.target
      const entryId = node.data.target?.sys.id
      const entryType = entry.__typename
      if (entry && entryId && entryType === 'ContentfulInstagramEmbed') {
        // We need to use a <span> instead of a <div> because Contentful wraps the embed entry with a <p> tag,
        // which would conflict with the block-level semantics of a <div>.
        return (
          <span
            dangerouslySetInnerHTML={{
              __html: hasMounted ? entry.embedCode.embedCode : ''
            }}
          />
        )
      }
      if (entry && entryId && entryType === 'ContentfulMediaWidgetEmbed') {
        // We need to use a <span> instead of a <div> because Contentful wraps the embed entry with a <p> tag,
        // which would conflict with the block-level semantics of a <div>.
        return (
          <span
            dangerouslySetInnerHTML={{
              __html: hasMounted ? entry.embedCode.embedCode : ''
            }}
          />
        )
      }
      if (entry && entryId && entryType === 'ContentfulImageLink') {
        const { url, title, image } = entry
        const { url: imageUrl, contentType } = image.file
        return (
          <a className='image-link' href={url}>
            <img
              alt={image.description || image.title || ''}
              src={imageUrl}
              title={image.title || ''}
            />
          </a>
        )
      }
    }
  }
}

export const UtilityBodyOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
      return (
        <img
          src={`https://${node.data.target.file.url}`}
          className='pb-2 pt-4 rounded-lg max-h-[486px] rounded-lg'
          alt={node.data.target.title}
        />
      )
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a href={uri} className='excluded-external-link-treatment'>
          {children}
        </a>
      )
    },
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ul className='list-disc list-outside pl-4 py-4'>{children}</ul>
    },
    [BLOCKS.HEADING_1]: (node: any, children: any) => {
      return <h1 className='pt-2'>{children}</h1>
    },
    [BLOCKS.HEADING_2]: (node: any, children: any) => {
      return <h2 className='pt-4'>{children}</h2>
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return (
        <h3 className='pt-10 !font-normal [&_span]:!font-normal'>{children}</h3>
      )
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      return <h4 className='pt-2'>{children}</h4>
    },
    [BLOCKS.HEADING_5]: (node: any, children: any) => {
      return <h5 className='pt-1'>{children}</h5>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='[&>p]:inline'>{children}</li>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <Typography allowHtml={false} as='p' classes='text-secondary pt-4'>
          {children}
        </Typography>
      )
    }
  }
}

export const UtilityListOptions = {
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderMark: {
    [MARKS.SUPERSCRIPT]: (text: any) => {
      return <sub className={`leading-[0] align-super text-[10px]`}>{text}</sub>
    },
    [MARKS.BOLD]: (text: any) => {
      return (
        <h5 className='font-sansPro text-black pb-2 font-sansPro !font-semibold text-[16px] sm:text-[22px] leaading-[24px] sm:leading-[32px]'>
          {text}
        </h5>
      )
    }
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a href={uri} className='font-normal'>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <Typography allowHtml={false} as='p' classes='text-secondary'>
          {children}
        </Typography>
      )
    }
  }
}

export const KeyTakeawaysCardText = {
  renderMark: {
    [MARKS.SUPERSCRIPT]: (text: any) => {
      return <sub>{text}</sub>
    },
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a href={uri} className='font-normal'>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-secondary !text-otl-p'
        >
          {children}
        </Typography>
      )
    }
  }
}

export const ResourceBodyOptions = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
      const asset = node.data.target
      const entryType = asset?.__typename
      if (entryType && entryType === 'ContentfulAsset') {
        const { url, contentType } = asset.file
        const isImage = contentType.includes('image')
        if (isImage) {
          return (
            <p className='rich-text-asset'>
              <img
                alt={asset.description || asset.title || ''}
                src={url}
                title={asset.title || ''}
              />
            </p>
          )
        }
      }
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a href={uri} className='font-normal underline'>
          {children}
        </a>
      )
    },
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ul className='list-disc list-outside ml-6 mb-4'>{children}</ul>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='[&>p]:inline'>{children}</li>
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='h3'
          classes='mb-6 [&:not(:first-child)]:mt-8'
          variation='h3'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Typography
          allowHtml={false}
          as='p'
          classes='text-secondary pb-4'
          variation='default'
        >
          {children}
        </Typography>
      ) : (
        <>{children}</>
      )
    }
  }
}
